import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LTText } from '@lambdatestincprivate/lt-components';
import { selectDetailAttempt, selectDetailStep, setDetailDrawer, usePackState } from '../../data/pack-store';
import { LogsDock } from './Dock/LogsDock';
import { closeDetailWithUrl } from '../../app/url-sync';
import { SCREENSHOT_PREFETCH_AHEAD, SCREENSHOT_PREFETCH_BEHIND } from '../../config';
import { artifactUrlCache } from '../../data/pack-store';
import { CompactHeader } from './Header/CompactHeader';
import { RcaBanner } from './RcaBanner';
import { ScreenshotPanel } from './Screenshot/ScreenshotPanel';
import { skinForEnv } from './Screenshot/MobileSkinOverlay';
import { StepsPanel } from './Steps/StepsPanel';
import { useAutoplay } from './Steps/useAutoplay';
import bannerStyles from './rca-banner.module.css';
import styles from './detail.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var LEFT_DEFAULT_PX = 440;

/** localStorage key for the dock's docking side (matches LogsDock's
 * `ev-detail-dock_<pos>` size keys). */
var DOCK_POSITION_KEY = 'ev-detail-dock_position';
var LEFT_MIN_PX = 360;
var LEFT_MAX_FRACTION = 0.6;

/**
 * Pixel-parity port of kaneai's TestInstanceWithDock page (minus the RCA
 * banner and the bottom dock, both excluded for now): compact header /
 * 16px-padded resizable split (v4 steps tree · screenshot panel). Fed
 * entirely from the pack; selection, autoplay, and the ?test= URL are the
 * only state.
 */
export function TestDetailPage(_ref) {
  var detail = _ref.detail;
  var titleRef = useRef(null);
  var pageRef = useRef(null);
  var stepsScrollRef = useRef(null);
  var _useState = useState(LEFT_DEFAULT_PX),
    _useState2 = _slicedToArray(_useState, 2),
    leftWidth = _useState2[0],
    setLeftWidth = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    fullscreen = _useState4[0],
    setFullscreen = _useState4[1];
  // Dock position is a lasting preference (like the dock sizes LogsDock
  // already stores) — remembered across details and sessions.
  var _useState5 = useState(function () {
      try {
        var v = window.localStorage.getItem(DOCK_POSITION_KEY);
        return v === 'bottom' || v === 'right' ? v : 'right';
      } catch (_unused) {
        return 'right';
      }
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    dockPosition = _useState6[0],
    setDockPositionState = _useState6[1];
  var setDockPosition = useCallback(function (pos) {
    setDockPositionState(pos);
    try {
      window.localStorage.setItem(DOCK_POSITION_KEY, pos);
    } catch (_unused2) {
      // Storage unavailable — position is per-session.
    }
  }, []);
  var dragRef = useRef(null);
  var dockOpen = detail.drawer.open;
  var loading = detail.phase === 'loading';
  var tree = detail.tree;
  var flatSteps = useMemo(function () {
    var _tree$flatSteps;
    return (_tree$flatSteps = tree === null || tree === void 0 ? void 0 : tree.flatSteps) !== null && _tree$flatSteps !== void 0 ? _tree$flatSteps : [];
  }, [tree]);
  var selectedKey = detail.selectedStepKey;

  // The pack's failure record for THIS test drives the RCA banner
  // (kaneai gates on rca_category/status; sealed packs have no
  // "Generate RCA" state — no record, no banner).
  var attemptsMeta = usePackState(function (s) {
    var _s$rows$get;
    return (_s$rows$get = s.rows.get(detail.testId)) === null || _s$rows$get === void 0 ? void 0 : _s$rows$get.attempts;
  });
  var attentionItem = usePackState(function (s) {
    var _s$attention$items$fi, _s$attention;
    return (_s$attention$items$fi = (_s$attention = s.attention) === null || _s$attention === void 0 ? void 0 : _s$attention.items.find(function (i) {
      return i.testId === detail.testId;
    })) !== null && _s$attention$items$fi !== void 0 ? _s$attention$items$fi : null;
  });
  var autoplay = useAutoplay({
    flatSteps: flatSteps,
    selectedKey: selectedKey,
    onSelect: selectDetailStep,
    scrollRef: stepsScrollRef
  });

  // Manual selection from any surface stops a running autoplay.
  var select = useCallback(function (key) {
    if (autoplay.active) autoplay.stop();
    selectDetailStep(key);
  }, [autoplay]);

  // kaneai's handleViewFailedStep: select the culprit and center it in
  // the steps panel (150ms lets the selection paint before the scroll
  // math). Orphan result steps have no row to scroll to — prefer the
  // record's step only when it renders, else the first failed ROW
  // (kaneai also scans for the first failed tree instruction), else the
  // orphan itself (selection still drives the screenshot pane).
  var viewFailedStep = useCallback(function () {
    var _ref2, _ref3;
    var recordStep = attentionItem !== null && attentionItem !== void 0 && attentionItem.stepId ? flatSteps.find(function (s) {
      return s.id === attentionItem.stepId;
    }) : undefined;
    var target = (_ref2 = (_ref3 = recordStep && !recordStep.orphan ? recordStep : undefined) !== null && _ref3 !== void 0 ? _ref3 : flatSteps.find(function (s) {
      return s.status === 'failed' && !s.orphan;
    })) !== null && _ref2 !== void 0 ? _ref2 : recordStep;
    if (!target) return;
    select(target.key);
    window.setTimeout(function () {
      var container = stepsScrollRef.current;
      var el = container === null || container === void 0 ? void 0 : container.querySelector("[data-step-key=\"".concat(CSS.escape(target.key), "\"]"));
      if (!container || !el) return;
      var elRect = el.getBoundingClientRect();
      var cRect = container.getBoundingClientRect();
      container.scrollBy({
        top: elRect.top - cRect.top - cRect.height / 2 + elRect.height / 2,
        behavior: 'smooth'
      });
    }, 150);
  }, [attentionItem, flatSteps, select]);
  var indexByKey = useMemo(function () {
    var m = new Map();
    flatSteps.forEach(function (s, i) {
      return m.set(s.key, i);
    });
    return m;
  }, [flatSteps]);
  var failIdx = useMemo(function () {
    return flatSteps.findIndex(function (s) {
      return s.status === 'failed';
    });
  }, [flatSteps]);

  // Unreached steps after the failure grey out (kaneai instance page);
  // authored verdicts are never repainted. Autoplay overlays on top.
  var displayed = useCallback(function (step) {
    var _indexByKey$get;
    var i = (_indexByKey$get = indexByKey.get(step.key)) !== null && _indexByKey$get !== void 0 ? _indexByKey$get : 0;
    var real = step.status === 'unknown' && failIdx >= 0 && i > failIdx ? 'disabled' : step.status;
    return autoplay.displayStatus(step.key, real);
  }, [indexByKey, failIdx, autoplay]);

  // Focus lands on the (sr-only) title when the detail opens.
  useEffect(function () {
    var _titleRef$current;
    (_titleRef$current = titleRef.current) === null || _titleRef$current === void 0 || _titleRef$current.focus();
  }, [detail.testId]);

  // Prefetch screenshots around the selection (cache dedups + owns URLs).
  useEffect(function () {
    var cache = artifactUrlCache();
    if (!cache || flatSteps.length === 0) return;
    var index = flatSteps.findIndex(function (s) {
      return s.key === selectedKey;
    });
    if (index < 0) return;
    for (var i = index - SCREENSHOT_PREFETCH_BEHIND; i <= index + SCREENSHOT_PREFETCH_AHEAD; i += 1) {
      var _flatSteps$i;
      var rel = (_flatSteps$i = flatSteps[i]) === null || _flatSteps$i === void 0 ? void 0 : _flatSteps$i.effectiveScreenshotPath;
      if (rel) void cache.getUrl(rel);
    }
  }, [flatSteps, selectedKey]);
  var onKeyDown = function onKeyDown(event) {
    if (event.key !== 'Escape') return;
    // Escape ladder: autoplay → fullscreen → logs dock → the detail itself.
    if (autoplay.active) {
      autoplay.stop();
    } else if (fullscreen) {
      setFullscreen(false);
    } else if (dockOpen) {
      setDetailDrawer(false);
    } else {
      closeDetailWithUrl();
    }
  };
  var clampLeft = useCallback(function (w) {
    var _pageRef$current$clie, _pageRef$current;
    var max = Math.max(LEFT_MIN_PX, ((_pageRef$current$clie = (_pageRef$current = pageRef.current) === null || _pageRef$current === void 0 ? void 0 : _pageRef$current.clientWidth) !== null && _pageRef$current$clie !== void 0 ? _pageRef$current$clie : window.innerWidth) * LEFT_MAX_FRACTION);
    return Math.min(max, Math.max(LEFT_MIN_PX, w));
  }, []);
  var onDividerPointerDown = function onDividerPointerDown(event) {
    dragRef.current = {
      startX: event.clientX,
      startWidth: leftWidth
    };
    var onMove = function onMove(e) {
      var drag = dragRef.current;
      if (!drag) return;
      setLeftWidth(clampLeft(drag.startWidth + (e.clientX - drag.startX)));
    };
    var _onUp = function onUp() {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', _onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', _onUp);
  };
  var onDividerKeyDown = function onDividerKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    var delta = event.key === 'ArrowRight' ? 24 : -24;
    setLeftWidth(function (w) {
      return clampLeft(w + delta);
    });
  };
  return (
    /*#__PURE__*/
    // The Escape handler needs key events from anywhere inside the page.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    _jsxs("div", {
      className: styles.page,
      ref: pageRef,
      onKeyDown: onKeyDown,
      children: [!fullscreen && /*#__PURE__*/_jsx(CompactHeader, {
        header: detail.header,
        titleRef: titleRef,
        onBack: closeDetailWithUrl,
        onEnterFullscreen: function onEnterFullscreen() {
          return setFullscreen(true);
        },
        attempt: detail.attempt,
        retryDirs: detail.retryDirs,
        attemptsMeta: attemptsMeta,
        onSelectAttempt: selectDetailAttempt
      }), !fullscreen && attentionItem && detail.phase !== 'error' && /*#__PURE__*/_jsx("div", {
        className: bannerStyles.bannerWrap,
        children: /*#__PURE__*/_jsx(RcaBanner, {
          item: attentionItem,
          onViewFailedStep: viewFailedStep
        })
      }), detail.phase === 'error' ? /*#__PURE__*/_jsxs("div", {
        className: styles.detailError,
        children: [/*#__PURE__*/_jsx(LTText, {
          text: "Couldn't read this test's detail from the pack.",
          variant: LTText.TextVariant.SUBHEADER_REGULAR,
          color: "var(--lt-text-secondary)"
        }), detail.error && /*#__PURE__*/_jsx(LTText, {
          text: detail.error.message,
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-secondary)"
        })]
      }) : /*#__PURE__*/_jsxs("div", {
        className: styles.content,
        style: {
          flexDirection: dockPosition === 'right' ? 'row' : 'column'
        },
        children: [/*#__PURE__*/_jsxs("div", {
          className: styles.split,
          children: [/*#__PURE__*/_jsx("div", {
            className: styles.splitLeft,
            style: {
              width: leftWidth
            },
            children: /*#__PURE__*/_jsx(StepsPanel, {
              tree: tree,
              loading: loading,
              selectedKey: selectedKey,
              onSelect: select,
              displayed: displayed,
              autoplayActive: autoplay.active,
              onToggleAutoplay: autoplay.toggle,
              scrollRef: stepsScrollRef
            })
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.splitDivider,
            role: "separator",
            "aria-orientation": "vertical",
            "aria-label": "Resize steps panel",
            "aria-valuenow": Math.round(leftWidth),
            tabIndex: 0,
            onPointerDown: onDividerPointerDown,
            onKeyDown: onDividerKeyDown,
            children: [/*#__PURE__*/_jsx("div", {
              className: styles.dividerLineTop,
              "aria-hidden": "true"
            }), /*#__PURE__*/_jsx("div", {
              className: styles.dividerLineBottom,
              "aria-hidden": "true"
            }), /*#__PURE__*/_jsx("div", {
              className: styles.dividerPill,
              "aria-hidden": "true"
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.splitRight,
            children: /*#__PURE__*/_jsx(ScreenshotPanel, {
              steps: flatSteps,
              selectedKey: selectedKey,
              resolution: detail.header.env.resolution,
              loading: loading,
              fullscreen: fullscreen,
              videoPath: detail.header.videoPath,
              videoUrl: detail.header.videoUrl,
              skin: skinForEnv(detail.header.env),
              dockOpen: dockOpen,
              displayStatus: displayed,
              onSelect: select,
              onToggleLogs: function onToggleLogs() {
                return setDetailDrawer(!dockOpen);
              },
              onExitFullscreen: function onExitFullscreen() {
                return setFullscreen(false);
              }
            })
          })]
        }), dockOpen && /*#__PURE__*/_jsx(LogsDock, {
          detail: detail,
          position: dockPosition,
          onPositionChange: setDockPosition
        })]
      })]
    })
  );
}

/** Store-connected mount point for AppShell. */
export function TestDetailMount() {
  var detail = usePackState(function (s) {
    return s.detail;
  });
  if (!detail) return null;
  return /*#__PURE__*/_jsx(TestDetailPage, {
    detail: detail
  });
}